import type { PromptRole, PromptTheme } from './types.js'
import { CSI, ESC, freezeStyle, STATUS_ICONS } from '@orkestrel/console'

// The constant DATA the pure terminal core reads: key decoding, rendering, and transport defaults.
// UPPER_SNAKE, `Object.freeze`d, every member exported. Control bytes are built with
// `String.fromCharCode` so no raw control character appears in source, and the escape lead and the
// CSI prefix come from `@orkestrel/console`, which publishes both.

// === Control bytes (named, no raw control characters in source)

/** Names the carriage return byte (`\r`, U+000D) — Enter on most terminals. */
export const RETURN = String.fromCharCode(13)
/** Names the line feed byte (`\n`, U+000A) — Enter on some terminals / pasted input. */
export const NEWLINE = String.fromCharCode(10)
/** Names the tab byte (`\t`, U+0009). */
export const TAB = String.fromCharCode(9)
/** Names the backspace byte (BS, U+0008) — Ctrl+H / some terminals' Backspace. */
export const BACKSPACE = String.fromCharCode(8)
/** Names the delete byte (DEL, U+007F) — the usual Backspace byte on a Unix TTY. */
export const DELETE = String.fromCharCode(127)
/** Names the space byte (U+0020). */
export const SPACE = ' '
/** Names the Ctrl+C byte (ETX, U+0003) — interrupt / cancel. */
export const CTRL_C = String.fromCharCode(3)
/** Names the Ctrl+D byte (EOT, U+0004) — end-of-transmission / finish (the editor's commit key). */
export const CTRL_D = String.fromCharCode(4)
/** Names the Ctrl+U byte (NAK, U+0015) — clear the current line. */
export const CTRL_U = String.fromCharCode(21)
/** Names the Ctrl+A byte (SOH, U+0001) — move to start of line. */
export const CTRL_A = String.fromCharCode(1)
/** Names the Ctrl+E byte (ENQ, U+0005) — move to end of line. */
export const CTRL_E = String.fromCharCode(5)

/**
 * Names the Single Shift Three lead (`ESCO`) — the alternate arrow-key prefix some terminals emit
 * (`ESC O A`). Built from the console module's own {@link import('@orkestrel/console').ESC}; the
 * navigation keys' CSI lead is that module's {@link import('@orkestrel/console').CSI}, which this
 * package reuses rather than redeclaring.
 */
export const KEY_SS3 = `${ESC}O`

/**
 * Holds the exact escape sequence to canonical key name table
 * {@link import('./helpers.js').parseKey} consults for the navigation and editing keys. Covers the
 * CSI form (`ESC[A`…) and the SS3 form (`ESCOA`…) of the arrows, plus the `home` / `end` / `delete`
 * CSI sequences with their numeric-tilde variants. The source of truth for the multi-byte key
 * decode; frozen.
 *
 * @remarks
 * Terminals disagree on these: a cursor key is `ESC[A` (normal) or `ESCOA` (application mode),
 * and Home / End / Delete each have a letter form (`ESC[H` / `ESC[F`) and a numeric form
 * (`ESC[1~` / `ESC[4~` / `ESC[3~`). Every accepted spelling maps to one name so a reducer never
 * sees the wire encoding.
 */
export const SEQUENCE_NAMES: Readonly<Record<string, string>> = Object.freeze({
	[`${CSI}A`]: 'up',
	[`${CSI}B`]: 'down',
	[`${CSI}C`]: 'right',
	[`${CSI}D`]: 'left',
	[`${KEY_SS3}A`]: 'up',
	[`${KEY_SS3}B`]: 'down',
	[`${KEY_SS3}C`]: 'right',
	[`${KEY_SS3}D`]: 'left',
	[`${CSI}H`]: 'home',
	[`${CSI}F`]: 'end',
	[`${CSI}1~`]: 'home',
	[`${CSI}4~`]: 'end',
	[`${CSI}3~`]: 'delete',
	[`${CSI}7~`]: 'home',
	[`${CSI}8~`]: 'end',
})

/**
 * Holds the control byte (or CRLF pair) to key descriptor table
 * {@link import('./helpers.js').parseKey} consults for the one-byte keys and the two-byte CRLF
 * Enter chunk. Each entry carries the canonical `name` and whether it is a `ctrl` combination. The
 * source of truth for that decode; frozen.
 *
 * @remarks
 * `return` / `newline` / `return + newline` all map to `return` (one canonical Enter name) — a
 * terminal or a paste can deliver Enter as `\r`, `\n`, or the `\r\n` pair in one chunk;
 * `delete` / `backspace` both map to `backspace` (the Backspace bytes); the Ctrl combos
 * (`c` / `d` / `u` / `a` / `e`) carry `ctrl: true` so a reducer can match
 * `key.ctrl && key.name === 'c'`. `escape` / `tab` / `space` are plain named keys.
 */
export const CONTROL_NAMES: Readonly<
	Record<string, { readonly name: string; readonly ctrl: boolean }>
> = Object.freeze({
	[RETURN]: Object.freeze({ name: 'return', ctrl: false }),
	[NEWLINE]: Object.freeze({ name: 'return', ctrl: false }),
	[`${RETURN}${NEWLINE}`]: Object.freeze({ name: 'return', ctrl: false }),
	[TAB]: Object.freeze({ name: 'tab', ctrl: false }),
	[ESC]: Object.freeze({ name: 'escape', ctrl: false }),
	[BACKSPACE]: Object.freeze({ name: 'backspace', ctrl: false }),
	[DELETE]: Object.freeze({ name: 'backspace', ctrl: false }),
	[SPACE]: Object.freeze({ name: 'space', ctrl: false }),
	[CTRL_C]: Object.freeze({ name: 'c', ctrl: true }),
	[CTRL_D]: Object.freeze({ name: 'd', ctrl: true }),
	[CTRL_U]: Object.freeze({ name: 'u', ctrl: true }),
	[CTRL_A]: Object.freeze({ name: 'a', ctrl: true }),
	[CTRL_E]: Object.freeze({ name: 'e', ctrl: true }),
})

// === Prompt defaults

/** Names the default mask glyph {@link import('./helpers.js').createPasswordState} uses — `*`. */
export const DEFAULT_MASK = '*'

// === Prompt-view icons

/**
 * Holds the terminal-owned glyphs {@link DEFAULT_PROMPT_THEME} assembles its `icons` from, beside
 * the console module's own success and error marks. Read only when the default theme is assembled;
 * a view reads its resolved theme and never this constant. Frozen.
 *
 * @remarks
 * - `question` — the leading mark on a prompt's message line.
 * - `pointer` — the cursor before the input / the focused choice row.
 * - `dot` / `selected` — an unfocused / focused row marker in a select list.
 * - `checked` / `unchecked` — a checked / unchecked box in a checkbox list.
 */
export const PROMPT_ICONS = Object.freeze({
	question: '?',
	pointer: '›',
	dot: '○',
	selected: '●',
	checked: '☑',
	unchecked: '☐',
})

// === The default prompt theme

/**
 * Holds every {@link import('./types.js').PromptRole}, in one frozen list — the role axis's source of
 * truth. {@link import('./helpers.js').createPromptTheme} walks it to merge a partial theme, and
 * a consumer building a complete role map reads it rather than retyping every name.
 */
export const PROMPT_ROLES: readonly PromptRole[] = Object.freeze([
	'question',
	'pointer',
	'message',
	'content',
	'success',
	'error',
	'selected',
	'focus',
	'hint',
	'muted',
	'description',
])

/**
 * Holds the {@link import('./types.js').PromptTheme} every prompt renders with unless its options supply
 * another — the glyph set assembled from {@link PROMPT_ICONS} plus the console
 * {@link import('@orkestrel/console').STATUS_ICONS} `success` / `error` marks, and the console
 * {@link import('@orkestrel/console').Style} each role is painted with. Deeply frozen through the
 * console module's own {@link import('@orkestrel/console').freezeStyle}; the baseline
 * {@link import('./helpers.js').createPromptTheme} merges a partial theme over.
 *
 * @remarks
 * The default roles reproduce the views' historical coloring exactly: `question` / `pointer` cyan,
 * `message` / `focus` bold, `success` / `selected` green, `error` red, `hint` / `muted` /
 * `description` dim, and `content` the EMPTY style — an empty style renders bare text, so primary
 * content keeps the bytes it had before it became themeable.
 * Two roles sharing a style today are still two roles — re-mapping one leaves the other alone.
 */
export const DEFAULT_PROMPT_THEME: PromptTheme = Object.freeze({
	icons: Object.freeze({
		question: PROMPT_ICONS.question,
		pointer: PROMPT_ICONS.pointer,
		dot: PROMPT_ICONS.dot,
		selected: PROMPT_ICONS.selected,
		checked: PROMPT_ICONS.checked,
		unchecked: PROMPT_ICONS.unchecked,
		success: STATUS_ICONS.success,
		error: STATUS_ICONS.error,
	}),
	roles: Object.freeze({
		question: freezeStyle({ foreground: 'cyan', attributes: [] }),
		pointer: freezeStyle({ foreground: 'cyan', attributes: [] }),
		message: freezeStyle({ attributes: ['bold'] }),
		content: freezeStyle({ attributes: [] }),
		success: freezeStyle({ foreground: 'green', attributes: [] }),
		error: freezeStyle({ foreground: 'red', attributes: [] }),
		selected: freezeStyle({ foreground: 'green', attributes: [] }),
		focus: freezeStyle({ attributes: ['bold'] }),
		hint: freezeStyle({ attributes: ['dim'] }),
		muted: freezeStyle({ attributes: ['dim'] }),
		description: freezeStyle({ attributes: ['dim'] }),
	}),
})

// === Broker + SSE-bridge defaults

/** Holds how long (ms) the {@link import('./types.js').PromptInterface} broker parks an unanswered form before it expires — 5 minutes. */
export const DEFAULT_PROMPT_TIMEOUT_MS = 300_000

/** Holds how long (ms) the {@link import('./types.js').PromptClientInterface} waits before each reconnect attempt — 2 seconds. */
export const DEFAULT_RECONNECT_DELAY_MS = 2_000

/**
 * Holds the SSE `event:` names the broker emits and the
 * {@link import('./types.js').PromptClientInterface} dispatches on — `pending`, `expire`, and
 * `destroy`. Frozen; the source of truth for the wire event vocabulary.
 *
 * @remarks
 * - `pending` — a serialized {@link import('./types.js').PendingForm} to dispatch and answer.
 * - `expire` — an `{ id }` payload: the broker expired or released a parked form (the client drops it).
 * - `destroy` — the broker is going away; the client disconnects (no auto-reconnect) but stays reusable.
 */
export const SSE_EVENTS = Object.freeze({
	pending: 'pending',
	expire: 'expire',
	destroy: 'destroy',
})

/**
 * Names the auth-token request header the {@link import('./types.js').PromptClientInterface} sends
 * when a `token` is configured — `x-orkestrel-token`.
 */
export const HEADER_TOKEN = 'x-orkestrel-token'

/** Names the `Accept` header value that opens the broker's SSE stream — `text/event-stream`. */
export const ACCEPT_EVENT_STREAM = 'text/event-stream'

/**
 * Sets the maximum number of characters the {@link import('./types.js').PromptClientInterface} lets its
 * SSE parser buffer before treating the stream as hostile — 1 MiB, comfortably above any
 * legitimate prompt payload. Passed as the `limit` to `createSSEParser` so an unterminated
 * or oversized `data:` field cannot grow the buffer without bound (a memory-exhaustion guard).
 */
export const SSE_BUFFER_LIMIT = 1_048_576
